/**
 * @file construct-tree-manager-btn.ts
 * @description 构件树管理器 - 负责管理构件树按钮和对话框
 * 
 * 功能概述：
 * 1. 在界面左上角显示构件树按钮
 * 2. 点击按钮打开构件树对话框，包含三个选项卡：楼层树、类型树、专业树
 * 3. 点击树节点时，如果节点有 ids，则高亮对应的 3D 模型构件
 * 
 * 数据流：
 * 1. 从 3D 引擎获取原始树数据 (getLevelTreeData/getTypeTreeData/getMajorTreeData)
 * 2. 通过 transformTreeData 转换为 SDK 标准的 TreeNodeConfig 格式
 * 3. 创建 BimTree 组件渲染树结构
 * 4. 用户点击节点时，通过 onNodeSelect 回调触发模型高亮
 */

import type { ButtonGroupColors, ButtonConfig } from '../components/button-group/index.type';
import { BaseManager } from '../core/base-manager';
import { ManagerRegistry } from '../core/manager-registry';
import { BimButtonGroup } from '../components/button-group';
import { BimTree } from '../components/tree';
import { TreeNodeConfig, TreeNodeCheckState } from '../components/tree/types';
import type { BimTreeNode } from '../components/tree/tree-node';
import { BimDialog } from '../components/dialog';
import { BimTab } from '../components/tab';
import { getIcon } from '../utils/icon-manager';

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 3D 引擎返回的原始树节点数据结构
 * 
 * @example
 * {
 *   name: "标高 1",
 *   id: null,
 *   ids: ["350518", "350520", ...],  // 构件 ID 数组，用于高亮模型
 *   children: [...],
 *   isLeaf: false
 * }
 */
interface EngineTreeNode {
    /** 节点显示名称 */
    name?: string;
    /** 节点 ID（可能为 null） */
    id?: string | null;
    /** 构件 ID 数组 - 用于调用 highlightModel 高亮模型 */
    ids?: string[] | null;
    /** 子节点列表 */
    children?: EngineTreeNode[] | null;
    /** 是否为叶子节点 */
    isLeaf?: boolean;
}

/**
 * 3D 引擎返回的原始模型数据结构（最外层）
 * 
 * @example
 * {
 *   name: "栈桥模型",
 *   url: "https://xxx.com/models/xxx/",  // 模型 URL，用于 highlightModel
 *   children: [...]
 * }
 */
interface EngineModelData {
    /** 模型显示名称 */
    name?: string;
    /** 模型资源 URL - 调用 highlightModel 时需要此参数 */
    url: string;
    /** 子节点列表 */
    children?: EngineTreeNode[] | null;
}

/**
 * 扩展的节点数据，包含模型 URL
 * 转换后存储在 TreeNodeConfig.data 中
 */
interface TransformedNodeData extends EngineTreeNode {
    /** 模型 URL - 从最外层 model.url 传递下来 */
    _modelUrl: string;
}

interface ModelParam {
    url: string;
    ids: number[];
}

// ============================================================================
// 工具函数
// ============================================================================

/**
 * 使用 SHA-256 算法对 ids 数组生成唯一哈希值
 * 
 * 为什么需要 hash：
 * - 原始数据中 node.id 可能为 null
 * - ids 数组可能很长（几百个元素），不适合直接作为 ID
 * - 需要一个稳定且唯一的标识符用于树组件的 nodeMap
 * 
 * @param ids - 构件 ID 数组
 * @returns 64 位十六进制哈希字符串
 * 
 * @example
 * hashIds(["350518", "350520"]) // => "a1b2c3d4e5f6..."
 */
function fnv1a32(input: string, seed: number): number {
    let h = seed >>> 0;
    for (let i = 0; i < input.length; i++) {
        const c = input.charCodeAt(i);
        h ^= c & 0xff;
        h = Math.imul(h, 0x01000193);
        h ^= (c >>> 8) & 0xff;
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

async function hashIds(ids: string[]): Promise<string> {
    const str = JSON.stringify(ids);
    const h1 = fnv1a32(str, 0x811c9dc5);
    const h2 = fnv1a32(str, 0x811c9dc5 ^ 0x9e3779b9);
    return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

/**
 * 递归收集节点及其所有子孙节点的 ids
 * 用于父级节点操作时，获取其下所有叶子节点对应的构件 ids
 */
function collectModelParams(node: BimTreeNode): ModelParam[] {
    const grouped = new Map<string, Set<number>>();

    const collect = (current: BimTreeNode): void => {
        const data = current.config.data as TransformedNodeData | undefined;
        const modelUrl = data?._modelUrl;
        if (modelUrl && data?.ids?.length) {
            let idSet = grouped.get(modelUrl);
            if (!idSet) {
                idSet = new Set<number>();
                grouped.set(modelUrl, idSet);
            }
            for (const rawId of data.ids) {
                const id = Number(rawId);
                if (Number.isFinite(id)) {
                    idSet.add(id);
                }
            }
        }

        for (const child of current.children || []) {
            collect(child);
        }
    };

    collect(node);

    const result: ModelParam[] = [];
    for (const [url, idSet] of grouped) {
        if (idSet.size > 0) {
            result.push({ url, ids: Array.from(idSet) });
        }
    }
    return result;
}

/**
 * 将 3D 引擎返回的原始树数据转换为 SDK 标准的 TreeNodeConfig 格式
 * 
 * 转换过程：
 * 1. 遍历最外层的模型数组，提取每个模型的 url
 * 2. 递归转换每个节点，将 url 注入到所有子节点的 data 中
 * 3. 如果节点有 ids 数组，使用 SHA-256 生成唯一 ID
 * 
 * 点击行为：
 * - 点击节点内容：触发 onNodeSelect（高亮+跳转）
 * - 点击箭头：展开/折叠子节点
 * 
 * 数据结构转换示意：
 * 
 * 输入：[{ name, url, children: [{ name, ids, children }] }]
 * 输出：[{ id, label, data: { ids, _modelUrl }, children }]
 * 
 * @param apiData - 3D 引擎返回的原始树数据
 * @returns 转换后的 TreeNodeConfig 数组
 */
let nodeIdCounter = 0;

async function transformTreeData(apiData: EngineModelData[]): Promise<TreeNodeConfig[]> {
    if (!apiData || apiData.length === 0) return [];

    /**
     * 递归转换单个节点
     * @param node - 原始节点数据
     * @param modelUrl - 从最外层传递的模型 URL
     */
    const transformNode = async (node: EngineTreeNode, modelUrl: string): Promise<TreeNodeConfig> => {
        const hasChildren = node.children && node.children.length > 0;
        
        // 生成节点 ID：优先使用 ids 哈希，其次使用原始 id，最后生成唯一 ID
        let id: string;
        if (node.ids?.length) {
            id = await hashIds(node.ids);
        } else if (node.id) {
            id = node.id;
        } else {
            id = `node_${++nodeIdCounter}`;
        }
        
        return {
            id,
            label: node.name || '未命名',
            expanded: false,
            checked: true,
            children: hasChildren
                ? await Promise.all(node.children!.map(child => transformNode(child, modelUrl)))
                : undefined,
            data: {
                ...node,
                _modelUrl: modelUrl
            } as TransformedNodeData
        };
    };

    // 遍历最外层模型数组
    return Promise.all(apiData.map(async (model) => {
        const hasChildren = model.children && model.children.length > 0;
        // ⭐ 提取模型 URL，将传递给所有子节点
        const modelUrl = model.url;
        
        return {
            id: modelUrl,
            label: model.name || '模型',
            expanded: true,
            checked: true,
            children: hasChildren
                ? await Promise.all(model.children!.map(child => transformNode(child, modelUrl)))
                : undefined,
            data: {
                _modelUrl: modelUrl
            } as TransformedNodeData
        };
    }));
}

// ============================================================================
// 管理器类
// ============================================================================

/**
 * 构件树管理器
 * 
 * 职责：
 * 1. 管理构件树按钮的生命周期（创建、显示/隐藏、销毁）
 * 2. 管理构件树对话框（打开、关闭、内容渲染）
 * 3. 处理树节点点击事件，调用 3D 引擎高亮模型
 * 
 * 使用示例：
 * ```typescript
 * const manager = new ConstructTreeManagerBtn(container);
 * manager.openConstructTreeDialog(); // 打开构件树对话框
 * ```
 */
export class ConstructTreeManagerBtn extends BaseManager {
    /** 按钮组实例 - 用于渲染构件树按钮 */
    private toolbar: BimButtonGroup | null = null;
    /** 按钮容器元素 */
    private toolbarContainer: HTMLElement | null = null;
    /** 主容器元素 - 按钮挂载的父容器 */
    private container: HTMLElement;
    /** 构件树对话框实例 */
    private dialog: BimDialog | null = null;

    /**
     * 创建构件树管理器
     * @param container - 按钮挂载的容器元素
     */
    constructor(container: HTMLElement, registry: ManagerRegistry) {
        super(registry);
        this.container = container;
        this.init();
    }

    /**
     * 初始化按钮
     * 创建按钮容器和按钮组，添加构件树按钮
     */
    private init() {
        // 创建按钮容器
        this.toolbarContainer = document.createElement('div');
        this.toolbarContainer.id = 'bim-construct-tree';
        this.container.appendChild(this.toolbarContainer);
        
        // 创建按钮组
        this.toolbar = new BimButtonGroup({
            container: this.toolbarContainer,
            showLabel: false,
            direction: 'column',
            position: 'top-left',
            align: 'vertical',
            expand: 'up',
            registry: this.registry
        });
        this.toolbar.init();
        this.toolbar.addGroup('construct-tree');
        
        // 添加构件树按钮
        this.toolbar.addButton({
            id: 'construct-tree-btn',
            groupId: 'construct-tree',
            type: 'button',
            label: 'construct-tree',
            icon: getIcon('目录树'),
            onClick: () => {
                if (this.registry.componentTreeDrawer) {
                    this.registry.componentTreeDrawer.toggle();
                    return;
                }
                this.openConstructTreeDialog();
            }
        });
        this.toolbar.render();
    }

    /**
     * 打开构件树对话框
     * 
     * 流程：
     * 1. 隐藏按钮组
     * 2. 从 3D 引擎获取三种树数据（楼层/类型/专业）
     * 3. 转换数据格式
     * 4. 创建三个 BimTree 实例
     * 5. 创建选项卡组件
     * 6. 创建对话框并显示
     */
    public async openConstructTreeDialog() {
        if (this.registry.componentTreeDrawer) {
            this.registry.componentTreeDrawer.open();
            return;
        }

        // 隐藏按钮组，避免遮挡对话框
        this.setVisible(false);

        // 从 3D 引擎获取原始树数据
        const levelTreeData = this.engineComponent?.getLevelTreeData() ?? [];
        const typeTreeData = this.engineComponent?.getTypeTreeData() ?? [];
        const majorTreeData = this.engineComponent?.getMajorTreeData() ?? [];

        // 调试日志：输出原始数据
        console.log('[ConstructTree] 原始数据 (Level):', levelTreeData);
        console.log('[ConstructTree] 原始数据 (Type):', typeTreeData);
        console.log('[ConstructTree] 原始数据 (Major):', majorTreeData);

        /**
         * 创建树组件
         * @param data - 原始树数据
         * @param label - 调试用标签
         */
        const createTree = async (data: any[], label: string) => {
            // 转换数据格式
            const transformedData = await transformTreeData(data);
            console.log(`[ConstructTree] 转换后数据 (${label}):`, transformedData);
            
            const tree = new BimTree({
                data: transformedData,
                checkable: true,
                indent: 0,
                enableSearch: true,
                checkStrictly: true,
                defaultExpandAll: true,
                
                /**
                 * 节点勾选回调 - 显示/隐藏模型构件
                 * 勾选 → showModel，取消勾选 → hideModels
                 */
                onNodeCheck: (node) => {
                    const modelParam = collectModelParams(node);
                    if (!modelParam.length) return;
                    
                    if (node.checkState === TreeNodeCheckState.Checked) {
                        this.engineComponent?.showModel(modelParam);
                    } else {
                        this.engineComponent?.hideModels(modelParam);
                    }
                },
                
                /**
                 * 节点选中回调 - 高亮并跳转到模型构件
                 */
                onNodeSelect: (node) => {
                    const modelParam = collectModelParams(node);
                    if (!modelParam.length) return;
                    
                    this.engineComponent?.unhighlightAllModels();
                    this.engineComponent?.highlightModel(modelParam);
                    this.engineComponent?.viewScaleToModel(modelParam);
                },
                
                // 再次点击已选中节点时取消高亮
                onNodeDeselect: () => {
                    this.engineComponent?.unhighlightAllModels();
                },
                
                onNodeExpand: () => {
                    this.dialog?.fitWidth();
                },
            });
            tree.init();
            return tree;
        };

        // 创建三个树实例
        const componentTree = await createTree(levelTreeData, 'Level');
        const typeTree = await createTree(typeTreeData, 'Type');
        const majorTree = await createTree(majorTreeData, 'Major');

        // 创建选项卡面板容器
        const componentPanel = document.createElement('div');
        componentPanel.className = 'construct-tab__panel-content';
        componentPanel.appendChild(componentTree.element);

        const typePanel = document.createElement('div');
        typePanel.className = 'construct-tab__panel-content';
        typePanel.appendChild(typeTree.element);

        const majorPanel = document.createElement('div');
        majorPanel.className = 'construct-tab__panel-content';
        majorPanel.appendChild(majorTree.element);

        // 创建选项卡组件
        const tabMount = document.createElement('div');
        tabMount.className = 'construct-tab__container';
        tabMount.style.height = '100%';
        tabMount.style.overflow = 'hidden';
        
        /**
         * 重置所有树的勾选状态并显示所有模型
         * 在 Tab 切换和对话框初始化时调用
         */
        const resetAllTrees = () => {
            this.engineComponent?.showAllModels();
            componentTree.checkAllNodes(true);
            typeTree.checkAllNodes(true);
            majorTree.checkAllNodes(true);
        };

        const tab = new BimTab({
            container: tabMount,
            tabs: [
                { id: 'component', title: 'tab.component', content: componentPanel },
                { id: 'type', title: 'tab.type', content: typePanel },
                { id: 'major', title: 'tab.major', content: majorPanel },
            ],
            activeId: 'component',
            onChange: () => {
                this.dialog?.fitWidth();
            }
        });
        tab.init();

        // BimTab 初始化时不触发 onChange，需要手动调用
        resetAllTrees();

        // 监听右键菜单"显示全部"事件
        const unsubscribeShowAll = this.registry.on('menu:show-all', () => {
            resetAllTrees();
        });

        // 创建对话框
        this.dialog = this.registry.dialog!.create({
            title: 'constructTree.title',
            minWidth: 320,
            height: 600,
            content: tabMount,
            position: { x: 20, y: 20 },
            resizable: false,
            onClose: () => {
                unsubscribeShowAll();
                tab.destroy();
                componentTree.destroy();
                typeTree.destroy();
                majorTree.destroy();
                this.setVisible(true);
            }
        });
        this.dialog?.fitWidth();
    }

    /** 刷新渲染 */
    public refresh() {
        this.toolbar?.render();
    }

    /** 销毁管理器 */
    public destroy() {
        this.toolbar?.destroy();
        this.toolbar = null;
        super.destroy();
    }

    /**
     * 添加按钮组
     * @param groupId - 组 ID
     * @param beforeGroupId - 插入位置
     */
    public addGroup(groupId: string, beforeGroupId?: string) {
        this.toolbar?.addGroup(groupId, beforeGroupId);
        this.toolbar?.render();
    }

    /**
     * 添加按钮
     * @param config - 按钮配置
     */
    public addButton(config: ButtonConfig) {
        this.toolbar?.addButton(config);
        this.toolbar?.render();
    }

    /**
     * 设置按钮可见性
     * @param id - 按钮 ID
     * @param v - 是否可见
     */
    public setButtonVisibility(id: string, v: boolean) {
        this.toolbar?.updateButtonVisibility(id, v);
    }

    /**
     * 设置是否显示标签
     * @param show - 是否显示
     */
    public setShowLabel(show: boolean) {
        this.toolbar?.setShowLabel(show);
    }

    /**
     * 设置按钮组可见性
     * @param visible - 是否可见
     */
    public setVisible(visible: boolean) {
        if (this.toolbarContainer) {
            this.toolbarContainer.style.visibility = visible ? 'visible' : 'hidden';
        }
    }

    /**
     * 设置背景颜色
     * @param color - 颜色值
     */
    public setBackgroundColor(color: string) {
        this.toolbar?.setBackgroundColor(color);
    }

    /**
     * 设置按钮组颜色
     * @param colors - 颜色配置
     */
    public setColors(colors: ButtonGroupColors) {
        this.toolbar?.setColors(colors);
    }
}
